import { Link } from "react-router-dom";
import SiteHeader from "@/components/feature/SiteHeader";
import SiteFooter from "@/components/feature/SiteFooter";
import BackToTop from "@/components/feature/BackToTop";
import FaqSection from "@/components/feature/FaqSection";
import SubscribeSection from "@/components/feature/SubscribeSection";
import usePageMeta from "@/hooks/usePageMeta";
import { siteInfo, readerVoices } from "@/mocks/site";
import { faqs } from "@/mocks/faq";

const contentRules = [
  {
    step: "01",
    title: "建立时间线",
    desc: "先把公开信息按发生顺序排列，标出关键节点，让事件的骨架先立起来，再讨论观点。",
  },
  {
    step: "02",
    title: "标注信源等级",
    desc: "区分「已核实」「二手转述」「待核实推测」三类信息，正文中直接标出，不混为一谈。",
  },
  {
    step: "03",
    title: "交叉比对叙述",
    desc: "同一事件至少呈现两方以上视角，指出彼此矛盾之处，而不是只挑选符合预设的一方。",
  },
  {
    step: "04",
    title: "保留修订记录",
    desc: "事件有后续就更新档案，并保留每一次修订的时间与内容，让读者看到结论是如何变化的。",
  },
];

export default function About() {
  usePageMeta({
    title: "关于我们 - 看料档案的内容原则与定位｜看料档案",
    description:
      "了解看料档案的定位与内容原则：专注事件深挖与结构化梳理，坚持信源分级、多角度比对与持续修订，为关注事件内幕与背后逻辑的读者提供可复查的深度内容。",
    keywords: "看料档案,关于我们,内容原则,事件深挖,看料黑料起底",
    canonicalPath: "/about",
    jsonLd: {
      "@context": "https://schema.org",
      "@graph": [
        {
          "@type": "AboutPage",
          name: "关于看料档案",
          url: "https://kanliao-archive.example/about",
        },
        {
          "@type": "Organization",
          name: siteInfo.name,
          email: siteInfo.email,
          foundingDate: siteInfo.since,
          description: siteInfo.intro,
        },
        {
          "@type": "BreadcrumbList",
          itemListElement: [
            { "@type": "ListItem", position: 1, name: "首页", item: "https://kanliao-archive.example/" },
            { "@type": "ListItem", position: 2, name: "关于我们", item: "https://kanliao-archive.example/about" },
          ],
        },
        {
          "@type": "FAQPage",
          mainEntity: faqs.map((faq) => ({
            "@type": "Question",
            name: faq.question,
            acceptedAnswer: { "@type": "Answer", text: faq.answer },
          })),
        },
      ],
    },
  });

  return (
    <div className="min-h-screen bg-background-50">
      <SiteHeader />
      <main>
        <section className="relative border-b border-background-200 bg-background-100">
          <div className="paper-grain pointer-events-none absolute inset-0" />
          <div className="relative mx-auto w-full max-w-7xl px-4 py-12 md:px-8 md:py-16">
            <nav aria-label="面包屑" className="flex flex-wrap items-center gap-2 text-xs text-foreground-500">
              <Link to="/" className="transition-colors hover:text-primary-600">
                首页
              </Link>
              <i className="ri-arrow-right-s-line" />
              <span className="text-foreground-800">关于我们</span>
            </nav>
            <h1 className="mt-5 font-heading text-3xl font-bold text-foreground-950 md:text-4xl">
              关于看料档案
            </h1>
            <p className="mt-5 max-w-3xl text-sm leading-8 text-foreground-600 md:text-base">
              {siteInfo.intro}
            </p>
            <div className="mt-8 grid grid-cols-2 gap-6 md:grid-cols-4">
              {siteInfo.stats.map((stat) => (
                <div key={stat.label} className="border-l-2 border-primary-500 pl-4">
                  <p className="font-heading text-2xl font-bold text-foreground-950 md:text-3xl">
                    {stat.value}
                    <span className="ml-1 text-sm font-normal text-foreground-500">{stat.unit}</span>
                  </p>
                  <p className="mt-1 text-xs text-foreground-500">{stat.label}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="py-16 md:py-20">
          <div className="mx-auto w-full max-w-7xl px-4 md:px-8">
            <div className="grid grid-cols-1 gap-12 lg:grid-cols-12 lg:gap-16">
              <div className="lg:col-span-5">
                <span className="text-xs font-medium tracking-[0.3em] text-primary-600">
                  POSITIONING
                </span>
                <h2 className="mt-4 font-heading text-2xl font-bold text-foreground-950 md:text-3xl">
                  我们做的是「档案」，不是「速报」
                </h2>
              </div>
              <div className="lg:col-span-7">
                <p className="text-sm leading-8 text-foreground-700 md:text-base">
                  在信息以秒计速流动的今天，最快的那一条往往最不完整。看料档案选择了一条更慢的路：等事件的基本事实沉淀下来，再动手梳理它的时间线与利益链。
                </p>
                <p className="mt-5 text-sm leading-8 text-foreground-700 md:text-base">
                  我们把每一篇内容都当作一份可以长期查阅的档案来对待。它需要经得起回看，也需要在信息更新时坦然标注修订。对读者而言，这意味着你看到的不是一次情绪化的判断，而是一份可以被复核的梳理结果。
                </p>
                <div className="mt-6 grid grid-cols-1 gap-5 sm:grid-cols-2">
                  {siteInfo.principles.map((principle) => (
                    <div key={principle.title} className="rounded-xl border border-background-200 bg-background-50 p-5">
                      <span className="flex h-10 w-10 items-center justify-center rounded-md bg-primary-50 text-primary-600">
                        <i className={`${principle.icon} text-xl`} />
                      </span>
                      <h3 className="mt-4 font-heading text-base font-semibold text-foreground-950">
                        {principle.title}
                      </h3>
                      <p className="mt-2 text-sm leading-7 text-foreground-600">{principle.desc}</p>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="border-y border-background-200 bg-background-100 py-16 md:py-20">
          <div className="mx-auto w-full max-w-7xl px-4 md:px-8">
            <span className="text-xs font-medium tracking-[0.3em] text-primary-600">
              CONTENT RULES
            </span>
            <h2 className="mt-4 font-heading text-2xl font-bold text-foreground-950 md:text-3xl">
              每一篇档案都要走过的四步
            </h2>
            <div className="mt-10 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4">
              {contentRules.map((rule) => (
                <div key={rule.step} className="rounded-xl border border-background-200 bg-background-50 p-6">
                  <span className="font-heading text-3xl font-bold text-background-400">{rule.step}</span>
                  <h3 className="mt-4 font-heading text-base font-semibold text-foreground-950">
                    {rule.title}
                  </h3>
                  <p className="mt-3 text-sm leading-7 text-foreground-600">{rule.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="py-16 md:py-20">
          <div className="mx-auto w-full max-w-7xl px-4 md:px-8">
            <span className="text-xs font-medium tracking-[0.3em] text-primary-600">
              READERS
            </span>
            <h2 className="mt-4 font-heading text-2xl font-bold text-foreground-950 md:text-3xl">
              读者怎么看我们
            </h2>
            <div className="mt-10 grid grid-cols-1 gap-6 sm:grid-cols-2">
              {readerVoices.map((voice) => (
                <figure
                  key={voice.name}
                  className="flex flex-col rounded-xl border border-background-200 bg-background-50 p-6"
                >
                  <span className="flex h-9 w-9 items-center justify-center rounded-md bg-accent-100 text-accent-800">
                    <i className="ri-double-quotes-l" />
                  </span>
                  <blockquote className="mt-4 flex-1 text-sm leading-8 text-foreground-700">
                    {voice.text}
                  </blockquote>
                  <figcaption className="mt-5 flex items-center gap-3 border-t border-background-200 pt-4">
                    <span className="flex h-9 w-9 items-center justify-center rounded-full bg-foreground-950 text-xs font-semibold text-background-50">
                      {voice.name.slice(0, 1)}
                    </span>
                    <span className="flex flex-col leading-tight">
                      <span className="text-sm font-medium text-foreground-950">{voice.name}</span>
                      <span className="text-xs text-foreground-500">{voice.role}</span>
                    </span>
                  </figcaption>
                </figure>
              ))}
            </div>
          </div>
        </section>

        <div id="faq" className="scroll-mt-24 border-t border-background-200 bg-background-100">
          <FaqSection
            faqs={faqs}
            heading="关于我们与内容方式的常见问题"
            intro="以下是读者最常问到的问题，涵盖内容来源、修订机制与线索提供方式。"
          />
        </div>

        <section className="py-16 md:py-20">
          <div className="mx-auto w-full max-w-7xl px-4 md:px-8">
            <div className="grid grid-cols-1 gap-8 lg:grid-cols-3">
              <div className="rounded-xl border border-background-200 bg-background-50 p-6">
                <span className="flex h-10 w-10 items-center justify-center rounded-md bg-primary-50 text-primary-600">
                  <i className="ri-mail-send-line text-xl" />
                </span>
                <h3 className="mt-4 font-heading text-base font-semibold text-foreground-950">
                  线索与投稿
                </h3>
                <p className="mt-2 text-sm leading-7 text-foreground-600">
                  欢迎提供带时间线与可核实节点的线索。为保证严谨性，我们不处理缺乏事实基础的匿名指控。
                </p>
                <p className="mt-3 text-sm text-foreground-800">{siteInfo.email}</p>
              </div>
              <div className="rounded-xl border border-background-200 bg-background-50 p-6">
                <span className="flex h-10 w-10 items-center justify-center rounded-md bg-primary-50 text-primary-600">
                  <i className="ri-error-warning-line text-xl" />
                </span>
                <h3 className="mt-4 font-heading text-base font-semibold text-foreground-950">
                  内容纠错
                </h3>
                <p className="mt-2 text-sm leading-7 text-foreground-600">
                  如果发现档案中的时间线或表述存在偏差，欢迎指出。核实后我们会更新正文并记录修订时间。
                </p>
                <p className="mt-3 text-sm text-foreground-800">{siteInfo.email}</p>
              </div>
              <div className="rounded-xl border border-background-200 bg-background-50 p-6">
                <span className="flex h-10 w-10 items-center justify-center rounded-md bg-primary-50 text-primary-600">
                  <i className="ri-team-line text-xl" />
                </span>
                <h3 className="mt-4 font-heading text-base font-semibold text-foreground-950">
                  内容合作
                </h3>
                <p className="mt-2 text-sm leading-7 text-foreground-600">
                  我们与多家行业研究者保持长期交流。如需内容转载或研究合作，请通过邮箱说明用途与范围。
                </p>
                <p className="mt-3 text-sm text-foreground-800">{siteInfo.email}</p>
              </div>
            </div>
          </div>
        </section>

        <SubscribeSection />
      </main>
      <SiteFooter />
      <BackToTop />
    </div>
  );
}